import React from 'react';
import { bool, func, number, object, shape, string } from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import styled from 'styled-components';
import _get from 'lodash/get';

import Typography from 'ravenjs/lib/Typography';
import { getThemeProps } from 'ravenjs/utils/theme';
import { renderCheck } from 'ravenjs/utils/viewport';

import NavLogo from 'components/NavLogo';
import { SPACING } from 'constants/spacing';
import { selectors as brandingSelectors } from 'modules/branding';
import { selectors as userSelectors } from 'modules/user';
import { isNavigationV2Enabled as isNavV2Enabled, isRootCompanyRole } from 'utils/company';
import { configContainsSku } from 'utils/configurations';

import { MIGRATED_COLORS } from 'constants/quartz/migrated_colors';
import NavBarIcons from './NavBarIcons';

const CoBrandingLogoStyled = styled.div`
    align-items: center;
    display: flex;
    ${({ theme }) => theme.media.up('md')`
        flex-direction: row;
        padding-left: 20px;
        padding-right: 5.125rem;
    `};
    ${({ theme }) => theme.media.down('md')`
        flex-direction: column;
        padding-right: ${({ padding }) => padding || '0.6rem'};
    `};
`;

const LogoBarStyled = styled.div`
    background: ${MIGRATED_COLORS.white};
    display: flex;
    justify-content: space-between;
    width: 100%;
`;

const NavLogoBackground = styled.div`
    background: ${({ logoBackground, whiteBackground }) =>
        whiteBackground
            ? MIGRATED_COLORS.white
            : logoBackground ||
              getThemeProps('AppNavigation.styles.backgroundColor', MIGRATED_COLORS.white)};
    ${({ theme }) => theme.media.up('md')`
        padding: ${({ padding }) => padding || '1rem 5.125rem'};
    `};
    ${({ theme }) => theme.media.down('md')`
        padding: ${({ padding }) => padding || '0.6rem'};
    `};
`;

const RootCompanyLogo = styled.img`
    ${({ theme }) => theme.media.up('md')`
        height: 30px;
        width: 96px;
    `};
    ${({ theme }) => theme.media.down('md')`
        height: 20px;
        width: 105px;
    `};
`;

const RightSection = styled.div`
    display: flex;
    align-items: center;
    ${({ theme }) => theme.media.up('md')`
        padding: 1rem 0;
    `};
    ${({ theme }) => theme.media.down('md')`
        padding: 0.6rem;
    `};
`;

const AppNavigationLogoBarV2 = props => {
    const {
        hasCustomLogo,
        logoBackground,
        logoUrl,
        logoPadding,
        onClickLogo,
        rootCompanyLogoUrl,
        userRoleType,
        whiteLogoBackground,
        partnerSettings,
        userConfiguration,
        history,
        userNav,
        user,
        isSoftLogin,
        userId,
    } = props;

    const [isNotRootCompanyRole, setIsNotRootCompanyRole] = React.useState(true);
    const displayPartnerOnlyLogo = _get(partnerSettings, 'displayPartnerOnlyLogo', false);

    const isNavigationV2Enabled = isNavV2Enabled();

    React.useEffect(() => {
        setIsNotRootCompanyRole(!isRootCompanyRole());
    }, [userRoleType]);

    const hasWhiteLabelSku = configContainsSku(userConfiguration, 'whitelabeling');

    const shouldShowCoBranding =
        !hasWhiteLabelSku && isNotRootCompanyRole && !displayPartnerOnlyLogo && hasCustomLogo;

    return (
        <LogoBarStyled className="LogoBar">
            <NavLogoBackground
                logoBackground={logoBackground}
                padding={logoPadding}
                whiteBackground={whiteLogoBackground}
            >
                <NavLogo
                    to={logoUrl}
                    onClick={onClickLogo}
                    hasCustomLogo={hasCustomLogo}
                    height="100%"
                    width="auto"
                    openLoginPrompt={() => {}}
                    isSoftLogin={false}
                />
            </NavLogoBackground>
            <RightSection>
                <NavBarIcons
                    isNavigationV2Enabled={isNavigationV2Enabled}
                    userNav={userNav}
                    user={user}
                    isSoftLogin={isSoftLogin}
                    userId={userId}
                    history={history}
                    showDivider={shouldShowCoBranding}
                />
                {!hasWhiteLabelSku &&
                    isNotRootCompanyRole &&
                    !displayPartnerOnlyLogo &&
                    hasCustomLogo && (
                        <CoBrandingLogoStyled padding={logoPadding}>
                            <Typography
                                fontSize={renderCheck('md', 'less') ? '0.6rem' : '0.8rem'}
                                gutterBottom="8px"
                                gutterRight={SPACING.M}
                                gutterTop="0.6rem"
                            >
                                Powered By
                            </Typography>
                            <RootCompanyLogo alt="logo" src={rootCompanyLogoUrl} />
                        </CoBrandingLogoStyled>
                    )}
            </RightSection>
        </LogoBarStyled>
    );
};

AppNavigationLogoBarV2.propTypes = {
    hasCustomLogo: bool,
    logoBackground: string,
    logoPadding: string,
    logoUrl: string,
    onClickLogo: func,
    rootCompanyLogoUrl: string,
    userRoleType: string,
    whiteLogoBackground: bool,
    partnerSettings: object,
    userConfiguration: shape({ whitelabeling: bool }),
    history: object,
    userNav: object,
    user: object,
    isSoftLogin: bool,
    userId: number.isRequired,
};

AppNavigationLogoBarV2.defaultProps = {
    hasCustomLogo: false,
    logoBackground: null,
    logoPadding: null,
    logoUrl: '',
    onClickLogo: () => {},
    rootCompanyLogoUrl: '',
    userRoleType: '',
    whiteLogoBackground: true,
    partnerSettings: {},
    userConfiguration: { whitelabeling: false },
    history: {},
    userNav: {},
    user: {},
    isSoftLogin: false,
};

const mapStateToProps = createStructuredSelector({
    hasCustomLogo: brandingSelectors.hasCustomLogo,
    rootCompanyLogoUrl: brandingSelectors.getRootCompanyLogoUrl,
    userRoleType: userSelectors.getUserRoleType,
    whiteLogoBackground: brandingSelectors.hasWhiteLogoBackground,
    partnerSettings: userSelectors.getPartnerSettings,
    userConfiguration: userSelectors.getUserConfiguration,
});

export { AppNavigationLogoBarV2 as AppNavigationLogoBarV2Unwrapped };
export default connect(mapStateToProps)(AppNavigationLogoBarV2);
